import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';

class AkCartUpsell extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.addEventListener('submit', this.#handleSubmit, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  #handleSubmit = async (event) => {
    event.preventDefault();

    const form = event.target;
    if (!form.reportValidity()) return;
    const button = form.querySelector('button[type="submit"]');
    const status = this.querySelector('[data-ak-upsell-status]');
    const formData = new FormData(form);
    const variantId = formData.get('id');
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    if (!variantId || button?.disabled) return;

    formData.append('sections', sectionIds.join(','));
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.root + 'cart/add.js', {
        ...config,
        headers: {
          ...config.headers,
          Accept: 'text/html',
        },
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'This item could not be added.');
      }

      if (status) status.textContent = 'Added to your cart.';
      this.dispatchEvent(
        new CartAddEvent({}, variantId.toString(), {
          source: 'ak-cart-upsell',
          itemCount: Number(formData.get('quantity') || 1),
          productId: this.dataset.productId,
          sections: result.sections,
        })
      );
    } catch (error) {
      if (status) status.textContent = error.message;
      button.disabled = false;
    } finally {
      button.removeAttribute('aria-busy');
    }
  };
}

class AkCartRecommendations extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    this.#load();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  async #request(intent) {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const root = window.Shopify?.routes?.root || Theme.routes.root || '/';
    const url = new URL(`${root}recommendations/products`, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('limit', '3');
    url.searchParams.set('intent', intent);

    const response = await fetch(url, { signal: this.#abortController.signal });
    if (!response.ok) return null;

    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    return documentFragment.querySelector('[data-ak-recommendations-fragment]');
  }

  async #load() {
    const list = this.querySelector('[data-ak-recommendations-list]');
    if (!list || !this.dataset.productId || !this.dataset.sectionId) return;

    list.setAttribute('aria-busy', 'true');

    try {
      let fragment = await this.#request('complementary');
      if (!fragment?.children.length) fragment = await this.#request('related');
      if (!fragment?.children.length) return;

      const pinnedCards = Array.from(list.querySelectorAll('[data-ak-threshold-card], [data-ak-curated-pairing-card]'));
      const pinnedProductIds = new Set(pinnedCards.map((card) => card.dataset.productId));
      const cards = Array.from(fragment.children)
        .filter((card) => !pinnedProductIds.has(card.dataset.productId))
        .map((card) => document.importNode(card, true));
      list.replaceChildren(...pinnedCards, ...cards);
      this.dataset.recommendationIntent = fragment.dataset.recommendationIntent || 'related';
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    } finally {
      list.setAttribute('aria-busy', 'false');
    }
  }
}

if (!customElements.get('ak-cart-upsell')) {
  customElements.define('ak-cart-upsell', AkCartUpsell);
}

if (!customElements.get('ak-cart-recommendations')) {
  customElements.define('ak-cart-recommendations', AkCartRecommendations);
}
